/**
 * AuthProvider — server-identity authority for the app (APP-W01a/W01b).
 *
 * Bootstrap rule: nothing is authenticated until the CURRENT server confirms
 * it. On mount the provider probes /auth/me through the raw shared transport
 * with a 10-second total deadline:
 *
 * - 200 + shape-validated payload → authenticated (generation advanced)
 * - 401 → unauthenticated (server says there is no session)
 * - timeout / malformed body / network failure / 5xx → unavailable —
 *   deliberately distinct from unauthenticated, and never treated as
 *   authenticated from any cached or persisted value
 *
 * Persisted user/org/token and public CSRF issuance never establish
 * authentication: nothing is restored from localStorage and nothing is
 * written to it (stale JWT-era entries are purged on server confirmation).
 * Login completion only causes server verification — the credentials-flow
 * response user/org are never trusted directly. Generation/attempt fencing
 * (identity-scope.ts) makes late or superseded probe results fail closed.
 *
 * W01d-D1 correction — ONE coherent transition authority: the provider
 * commits EVERY confirmed identity change onto the SHARED app-wide
 * `appIdentityScope` (confirmIdentity / collapseIdentity). The app-level
 * private-state generation consumers (toast ownership, cache purge, private
 * subtree keying) listen to that ONE scope, so the generation can never
 * drift from the confirmed AuthProvider identity state. A full A→B
 * transition or a logout synchronously advances the shared scope, so stale
 * toasts/timers and in-flight cache/state of the departed identity are
 * cleared exactly at the confirmed transition, and no second independent
 * generation system exists.
 *
 * APP-W01b:
 * - the API layer reports structured identity outcomes (no unscoped 401
 *   event); the provider applies them ONLY when the outcome belongs to the
 *   CURRENT identity generation (stale-generation reactions never commit);
 * - logout reports a truthful revocation state: confirmed-revoked,
 *   unconfirmed (probe could not establish), or probe-confirmed-absent;
 * - logout never recursively re-enters itself, and never claims the client
 *   cancellation stopped server work;
 * - CSRF token/promise state is generation-scoped (see csrf.ts).
 *
 * The context, hooks, and persisted-state helpers live in auth.ts (this
 * file only exports the component — react-refresh/only-export-components).
 */

import { useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import {
  AuthContext,
  AuthContextValue,
  AUTH_STORAGE_KEY,
  isServerIdentityPayload,
  LogoutRevocationState,
  type AuthOrg,
  type AuthStatus,
  type AuthUser,
} from './auth';
import {
  appIdentityScope,
  confirmIdentity,
  collapseIdentity,
} from './identity-scope';
import { boundedFetch, boundedAuthDeadline, API_TOTAL_DEADLINE_MS, IDENTITY_PROBE_TIMEOUT_MS, rawFetch } from './transport';
import {
  fetchCsrfToken,
  clearCsrfToken,
  getCsrfToken,
  readCsrfCookie,
  hasCsrfCookie,
  notifyCsrfGeneration,
} from './csrf';
import { setAuthOutcomeHandler, setIdentityGenerationSource, setAmbiguousIdentityProbe, probeAmbiguousAuthIdentity } from './api';
import { getAuthenticatingApiOrigin } from './api-config';
import { API_TOTAL_DEADLINE_MS as API_BUDGET_MS } from './transport';

// Non-null only for authenticating builds. Null (preview / fail-closed
// config) means no credentialed request is ever issued from this provider.
const API_BASE = getAuthenticatingApiOrigin();

const EMPTY_IDENTITY = { token: null, user: null, org: null } as const;

interface IdentityValue {
  token: string | null;
  user: AuthUser | null;
  org: AuthOrg | null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [identity, setIdentity] = useState<IdentityValue>(EMPTY_IDENTITY);
  // Explicit identity state (APP-W01a). A non-authenticating build never
  // probes and never authenticates: fail closed to unavailable (the root
  // renders the W06a neutral screen instead of the outlet in that case).
  const [status, setStatus] = useState<AuthStatus>(() => (API_BASE ? 'verifying' : 'unavailable'));
  // W01d-D1: identity AUTHORITY is the shared appIdentityScope — the
  // provider reads its generation for fencing but advances it through the
  // confirmIdentity/collapseIdentity seams only (one transition authority;
  // no second independent generation system).
  /** Flags a logout request in flight — guards recursive logout re-entry. */
  const logoutInFlightRef = useRef(false);

  const commit = useCallback((next: AuthStatus, value: IdentityValue | null) => {
    setIdentity(value ?? EMPTY_IDENTITY);
    setStatus(next);
  }, []);

  /**
   * Server verification probe. Establishes identity only from a current,
   * shape-validated /auth/me response; every failure mode lands on its
   * explicit fail-closed state. Never throws; never commits when
   * superseded (newer transition), aborted (unmount) or stale.
   */
  const verify = useCallback(
    async (options: { signal?: AbortSignal } = {}): Promise<void> => {
      // Non-authenticating build: never ask the server who we are. Without a
      // server response nothing can be authenticated — fail closed.
      if (!API_BASE) {
        commit('unavailable', null);
        return;
      }

      // W01d-D1: capture the attempt against the SHARED scope — the same
      // authority every consumer (toast, cache, subtree key) reads.
      const scope = appIdentityScope;
      const generation = scope.current().generation;
      const attempt = scope.beginAttempt();
      const controller = new AbortController();
      const unregisterAbort = scope.registerAbort(generation, () => controller.abort());

      // Forward the caller-owned abort (bootstrap/unmount controller) into
      // the SAME scope-registered controller: one abort seam reaches the
      // transport, so an unmount can actually cancel the in-flight probe.
      const forwardCallerAbort = options.signal ? () => controller.abort() : null;
      if (options.signal && forwardCallerAbort) {
        if (options.signal.aborted) forwardCallerAbort();
        else options.signal.addEventListener('abort', forwardCallerAbort);
      }

      try {
        // The CSRF cookie (24h) expires long before the session cookie
        // (30d), so a missing CSRF cookie is NOT proof there is no session —
        // and the session cookie is httpOnly, invisible to JS. Fetch a fresh
        // CSRF token first (public issuance — NOT identity), then probe
        // /auth/me unconditionally; an idle-but-valid session recovers.
        if (!hasCsrfCookie()) {
          await fetchCsrfToken();
        }

        const outcome = await rawFetch(`${API_BASE}/auth/me`, {
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          timeoutMs: IDENTITY_PROBE_TIMEOUT_MS,
        });

        // Fail closed: a result from a superseded attempt, an aborted
        // generation or an unmounted provider may never commit.
        if (!scope.isCurrentAttempt(attempt)) return;
        if (!scope.isCurrent(generation)) return;
        if (outcome.kind === 'aborted') return; // a newer transition owns the state

        if (outcome.kind !== 'response') {
          commit('unavailable', null);
          return;
        }

        if (outcome.status === 401) {
          commit('unauthenticated', null);
          return;
        }

        if (!outcome.ok) {
          commit('unavailable', null);
          return;
        }

        let parsed: unknown;
        try {
          parsed = JSON.parse(outcome.bodyText);
        } catch {
          commit('unavailable', null);
          return;
        }
        if (!isServerIdentityPayload(parsed)) {
          commit('unavailable', null);
          return;
        }

        // Server-confirmed identity: advance the SHARED generation (this
        // aborts any older-generation in-flight work and fences older
        // attempts), then commit. Re-confirmation with identical ids still
        // advances — permission changes invalidate everything captured
        // before. The toast/cache/private-subtree consumers are notified
        // through the SAME advance (W01d-D1 single authority).
        const advanced = confirmIdentity(parsed.user.userId, parsed.org.orgId);
        // Forward the generation into the CSRF scoping seam (W01b): token,
        // in-flight promise and cleanup are partitioned per identity.
        notifyCsrfGeneration(advanced.generation);
        commit('authenticated', {
          token: null, // No JWT needed — session cookie handles auth
          user: parsed.user,
          org: parsed.org,
        });
        // Cookie session is authoritative — drop any stale JWT-era entry.
        localStorage.removeItem(AUTH_STORAGE_KEY);
        // Ensure the CSRF token is ready for mutations (skip if the
        // pre-probe fetch above already populated it).
        if (!getCsrfToken()) {
          fetchCsrfToken().catch(() => { /* non-fatal */ });
        }
      } finally {
        unregisterAbort();
        if (forwardCallerAbort && options.signal) {
          options.signal.removeEventListener('abort', forwardCallerAbort);
        }
      }
    },
    [commit]
  );

  /**
   * Structured identity reaction from the shared API transport (apiV2Fetch).
   * Applied ONLY to the CURRENT generation — a stale generation's 401 never
   * commits an identity change.
   *
   * Ownership is captured ONCE at admission, inside the api seam, BEFORE any
   * wire I/O (api.ts `captureAdmissionGeneration` at the entry of
   * `apiV2Fetch`/`apiV2FetchWithProbe`), then threaded explicitly through
   * every auth result that logical request produces. Here we only CHECK that
   * ownership against the SHARED scope: the outcome's owning generation must
   * EQUAL the live generation. A stale A request that settles while B is
   * current carries A's generation, so it can never collapse, expire, replace
   * or emit a transition against B.
   *
   * Permission-denied and caller-aborted never react. The ambiguous-legacy-401
   * case stays local (W01b).
   */
  const reactToAuthOutcome = useCallback(
    (outcome: { code: string; status?: number; generation?: number }) => {
      if (!API_BASE) return;
      if (outcome.code === 'session-expired') {
        const scope = appIdentityScope;
        const current = scope.current();
        // Exact generation fencing (strict equality, not an ordering test):
        // the outcome belongs to the identity generation that OWNED the
        // logical request at admission. If that generation is not the live
        // one, the reaction is stale and is refused outright; an outcome with
        // no generation attached is likewise treated as stale and never
        // reacts.
        if (typeof outcome.generation !== 'number') return;
        if (outcome.generation !== current.generation) return;
        if (!current.userId) return; // nothing verified and alive
        collapseIdentity();
        commit('unauthenticated', null);
        localStorage.removeItem(AUTH_STORAGE_KEY);
        clearCsrfToken();
      }
      // 'auth-ambiguous-401', 'permission-denied', 'caller-aborted',
      // 'deadline-exceeded', 'network-disconnect', 'csrf-invalid', 'error':
      // never a global identity action. Callers decide local UX.
    },
    [commit]
  );

  // ONE provider-scoped registered handler (module-level in api.ts); the
  // handler's identity checks are generation-based so a stale outcome
  // cannot cross identity boundaries. Mounted once per provider instance.
  useEffect(() => {
    if (!API_BASE) return;
    setIdentityGenerationSource(() => appIdentityScope.current().generation);
    setAuthOutcomeHandler(reactToAuthOutcome);
    // W01b-B2: install the REAL ambiguous-401 identity probe — one raw
    // /auth/me probe, bounded deadline. W01b-B1: the probe is owned by the
    // generation that was admitted with the logical request, NOT by whatever
    // identity is current when the probe runs. A result whose owning
    // generation is no longer live can never apply — and a probe whose
    // ownership is already stale is refused before the raw probe is issued.
    setAmbiguousIdentityProbe(async (ownerGeneration: number | null, remainingMs: number) => {
      // No ownership recorded at admission (no confirmed identity) — this
      // request belongs to nobody, so it may not interrogate the server.
      if (ownerGeneration == null) return false;
      // Stale OWNERSHIP fence: the identity that owned this request has
      // already departed. Do not probe on behalf of the new identity.
      if (!appIdentityScope.isCurrent(ownerGeneration)) return false;
      const sessionPresent = await probeAmbiguousAuthIdentity(
        Math.min(remainingMs, API_BUDGET_MS)
      );
      // The identity transitioned while the probe was in flight: the result
      // still belongs to a departed generation and can never apply.
      if (!appIdentityScope.isCurrent(ownerGeneration)) return false;
      return sessionPresent;
    });
    return () => {
      setAuthOutcomeHandler(null);
      setIdentityGenerationSource(() => null);
      setAmbiguousIdentityProbe(null);
    };
  }, [reactToAuthOutcome]);

  const login = useCallback(
    (_token: string | null, _user: AuthUser, _org: AuthOrg) => {
      // APP-W01a: login completion must cause server verification — the
      // supplied user/org are never trusted and never persisted. The old
      // identity ends here: the SHARED transition invalidation aborts any
      // in-flight work of the previous generation so a late response cannot
      // repopulate authority, and the private gate stays closed until
      // /auth/me confirms.
      collapseIdentity();
      setStatus('transitioning');
      void verify();
    },
    [verify]
  );

  /**
   * Logout, with a truthful revocation report (APP-W01b stage 5).
   *
   * Local sign-out always commits IMMEDIATELY (identity/private data hidden
   * before any network outcome, on the SHARED generation authority) and then
   * posts /auth/logout exactly once. Revocation is then reported truthfully:
   * - revocation-confirmed: the server responded ok;
   * - revocation-unconfirmed: the POST failed (timeout/network/ambiguous);
   * - session-already-absent: the server answered 401-ish for the current
   *   identity — provable server-side absence.
   *
   * The response itself NEVER feeds back into logout()'s own recursive
   * dispatch — this raw call never throws, and the outcome handler is the
   * only identity-reactive seam (with the generation already collapsed, a
   * session-expired response from a CURRENT generation cannot be received
   * here anyway since advance() already superseded this attempt).
   */
  const logout = useCallback(async (): Promise<LogoutRevocationState> => {
    if (logoutInFlightRef.current) {
      // Explicit only — no implicit re-entry, no retry loop.
      return 'revocation-unconfirmed';
    }
    logoutInFlightRef.current = true;
    try {
      // Transition invalidation on the SHARED authority (W01d-D1): abort
      // in-flight probes of the old identity and synchronously clear its
      // private toast/timer state — a late success can never resurrect
      // authority after logout, and no stale toast survives into the
      // post-transition surface.
      collapseIdentity();
      commit('unauthenticated', null);
      localStorage.removeItem(AUTH_STORAGE_KEY);
      // Non-authenticating build: only local state is cleared; no request and
      // no CSRF clear (W06a boundary — this build never issued one).
      if (!API_BASE) {
        return 'revocation-unconfirmed';
      }

      // POST /auth/logout carries the captured token; clear the local CSRF
      // state AFTER capture so the server-side double-submit still matches.
      const csrf = getCsrfToken() ?? readCsrfCookie();
      clearCsrfToken();

      // Raw transport (boundedFetch) — NOT apiV2Fetch: the shared api seam
      // would already be generation-fenced (the advance above collapsed the
      // identity), but the direct raw call keeps the logout owner explicit
      // and avoids the outcome handler being invoked for a response that
      // belongs to a generation this provider already ended.
      const outcome = await boundedFetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        deadlineMs: boundedAuthDeadline(API_TOTAL_DEADLINE_MS),
        headers: csrf ? { 'X-CSRF-Token': csrf } : {},
      });

      // Truthful reporting:
      if (outcome.kind === 'response' && outcome.ok) {
        return 'revocation-confirmed';
      }
      if (outcome.kind === 'response' && outcome.status === 401) {
        // Server proves the session was already gone.
        return 'session-already-absent';
      }
      // Unconfirmed — the client never claims the cancellation stopped
      // server work; the cookie may still be live server-side.
      return 'revocation-unconfirmed';
    } finally {
      logoutInFlightRef.current = false;
    }
  }, [commit]);

  const updateUser = useCallback((partial: Partial<AuthUser>) => {
    setIdentity((prev) => {
      if (!prev.user) return prev;
      // In-memory only: persisted identity never carries authority (APP-W01a).
      return { ...prev, user: { ...prev.user, ...partial } };
    });
  }, []);

  // Bootstrap probe: runs once on mount. The session cookie is httpOnly, so
  // there is no client-side signal that could rule out a live session
  // without asking the server. The controller aborts on unmount — a late
  // probe response after unmount can never commit.
  useEffect(() => {
    const controller = new AbortController();
    void verify({ signal: controller.signal });
    return () => controller.abort();
  }, [verify]);

  // Structured-outcome identity reactions are wired through the api seam
  // (above). No unscoped auth:logout listener remains (APP-W01b removal).

  const isAuthenticated = status === 'authenticated';
  const authResolved =
    status === 'authenticated' || status === 'unauthenticated' || status === 'unavailable';

  const value = {
    ...identity,
    isAuthenticated,
    authResolved,
    status,
    login,
    logout,
    updateUser,
    revalidateAuth: verify,
  } satisfies Omit<AuthContextValue, 'logout'> & {
    logout: () => Promise<LogoutRevocationState>;
  };

  // The context value carries the truthful revocation to W01d via logout's
  // own promise return; the context keeps the public shape (W01d will add
  // the visible surface through the proper AuthContextValue seam).
  const ctx = value;

  return (
    <AuthContext.Provider value={ctx}>
      {children}
    </AuthContext.Provider>
  );
}
